import Swal from 'sweetalert2';

// Expired-date guard for a status change. Moved verbatim from
// DashboardSurvey.handleClickcheck so the list and the detail page share it.
// Only "Open" (Live) is checked: a Live survey past its expiry date is blocked
// with a warning. Any open react-bootstrap modal is pushed behind the Swal
// while it shows, then restored.
export const canSetStatus = (expiryDate: string | null | undefined, status: string): boolean => {
    const allModals = document.getElementsByClassName('fade modal show');
    if (status != "Open") { return true; }
    const changedExpireDate = expiryDate ? new Date(expiryDate) : null;
    const todayDate = new Date();
    const settingModalZIndex = (zIndex: string) => {
        Array.from(allModals).forEach(modal => {
            (modal as HTMLElement).style.zIndex = zIndex;
        });
    };
    if (changedExpireDate && changedExpireDate < todayDate) {
        settingModalZIndex('0');
        // Promise.resolve(): CRA's Jest runs with resetMocks, so a mocked
        // Swal.fire returns undefined in tests. In the app Swal.fire already
        // returns a Promise, so behaviour is unchanged.
        Promise.resolve(Swal.fire({
            icon: 'warning',
            title: 'Expired',
            text: 'The expiry date is in the past. Please correct the date',
        })).then(() => {
            settingModalZIndex('');
        });
        return false;
    } else {
        settingModalZIndex('');
        return true;
    }
};

// "Ready for Live" rule shared with the backend guards. A market (child
// survey) is ready when it is a unique-links market (exempt), or its effective
// Live URL is non-blank: its own url, else the parent's (the existing
// inherit-on-blank rule). Returns the labels (shortName, else country) of the
// markets that are NOT ready. A parent target is checked through every live
// (not deleted) child, since a parent status cascades to all of them.
const isBlank = (v: any): boolean => v === undefined || v === null || String(v).trim() === '';

export const marketsNotReadyForLive = (target: any, parent: any, children: any[]): string[] => {
    if (!target) return [];
    const isChild = !!(parent || target.parentid);
    const markets: any[] = isChild
        ? [target]
        : (children || []).filter((c: any) => c && !c.deletedAt);
    const parentUrl = isChild ? parent?.url : target.url;
    return markets
        .filter((m: any) => !m.useUniqueLinks && isBlank(m.url) && isBlank(parentUrl))
        .map((m: any) => m.shortName || m.country);
};

// A parent project with NO markets has nothing for marketsNotReadyForLive to
// check, so going Live needs its own Live URL (unless it uses unique links).
// Mirrors projectWithoutMarketsNotReady in the backend's readyForLive.js.
export const projectWithoutMarketsNotReady = (parent: any, children: any[]): string[] => {
    if (!parent || parent.parentid) return [];
    if ((children || []).some((c: any) => c && !c.deletedAt)) return [];
    if (parent.useUniqueLinks || !isBlank(parent.url)) return [];
    return [parent.projectname || parent.projectId || parent.name || 'this project'];
};

export const liveUrlMessage = (labels: string[]): string =>
    `Add a Survey Live URL for ${labels.join(', ')} before setting the survey Live.`;
